import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Controller, useForm, useWatch, type FieldError, type UseFormGetValues, type UseFormRegister } from "react-hook-form";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { apiRequest, clearStoredToken, getStoredToken, setStoredToken } from "../api/client";
import { leadCatBrand } from "../brand/brandConfig";
import { BusinessCategoryCombobox } from "../components/BusinessCategoryCombobox/BusinessCategoryCombobox";
import { BrandLogo } from "../components/BrandLogo/BrandLogo";
import { Button } from "../components/Button/Button";
import { InlineNotice } from "../components/Feedback/InlineNotice";
import { recordAnalyticsEvent } from "../marketing/analytics";
import { PasswordInput } from "../components/PasswordInput/PasswordInput";
import {
  authError,
  isValidEmail,
  normalizeEmail,
  normalizePhone,
  passwordRules,
  signupBusinessCategories,
  signupLeadSources,
} from "./format";
import { useSharedLoginAvailable } from "../platform/SharedLoginContext";
import { useOtpCooldown } from "./useOtpCooldown";
import {
  changePasswordSchema,
  forgotPasswordSchema,
  loginSchema,
  resetPasswordSchema,
  signupSchema,
  type AuthResponse,
  type ChangePasswordValues,
  type CurrentLegalDocumentsResponse,
  type ForgotPasswordValues,
  type LoginValues,
  type OtpRequestResponse,
  type OtpVerifyResponse,
  type PasswordActionCompleteResponse,
  type PasswordActionDetails,
  type PasswordEmailResponse,
  type ResetPasswordValues,
  type SignupPayload,
  type SignupValues,
  type VerifiedOtp,
} from "./types";
import "./AuthPage.css";

const EMPTY_VERIFICATION: VerifiedOtp = { destination: "", token: "" };

function AuthShell({
  pageTitle,
  signup = false,
  children,
}: {
  pageTitle: string;
  signup?: boolean;
  children: ReactNode;
}) {
  useEffect(() => {
    document.title = `${pageTitle} | ${leadCatBrand.products.crm.name}`;
  }, [pageTitle]);

  return (
    <main className={`rt-auth-shell ${signup ? "signup" : ""}`}>
      <div className="rt-auth-frame">
        <aside className="rt-auth-brand-panel" aria-label="LeadCat CRM product information">
          <Link className="rt-auth-logo-link" to="/" aria-label="LeadCat CRM homepage">
            <BrandLogo className="rt-auth-brand-logo" product="crm" size="medium" />
          </Link>
          <div className="rt-auth-brand-message">
            <span>Private CRM workspace</span>
            <h2>Turn every conversation into a clear next action.</h2>
            <p>Bring Instagram, Facebook and WhatsApp enquiries into one organised follow-up workspace.</p>
          </div>
          <ul className="rt-auth-benefits" aria-label="LeadCat CRM workspace benefits">
            <li><span aria-hidden="true">✓</span> Tenant-private customer data</li>
            <li><span aria-hidden="true">✓</span> Role-aware team access</li>
            <li><span aria-hidden="true">✓</span> Secure email verification</li>
          </ul>
          <div className="rt-auth-brand-footer">
            <strong>{leadCatBrand.tagline}</strong>
            <small>{leadCatBrand.legalLine}</small>
          </div>
        </aside>
        <section className={`rt-auth-card ${signup ? "signup" : ""}`}>
          <Link className="rt-auth-back-link" to="/"><span aria-hidden="true">←</span> Back to homepage</Link>
          {children}
        </section>
      </div>
    </main>
  );
}

function fieldError(error?: FieldError) {
  return error?.message ? <small className="rt-auth-field-error">{error.message}</small> : null;
}

function safeReturnPath(value: unknown): string {
  return typeof value === "string" && value.startsWith("/") && !value.startsWith("//") ? value : "/dashboard";
}

export function LoginPage() {
  const sharedLoginAvailable = useSharedLoginAvailable();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const locationState = location.state as { from?: string; notice?: string } | null;
  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });
  const login = useMutation({
    mutationFn: (values: LoginValues) => apiRequest<AuthResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email: normalizeEmail(values.email), password: values.password }),
    }),
    onSuccess: (result) => {
      queryClient.clear();
      setStoredToken(result.token);
      const returnPath = safeReturnPath(locationState?.from);
      if (result.user.must_change_password) {
        navigate("/change-password", {
          replace: true,
          state: { from: returnPath },
        });
        return;
      }
      navigate(returnPath, { replace: true });
    },
  });

  const nativeLoginForm = (
      <form className="rt-auth-stack" onSubmit={form.handleSubmit((values) => login.mutate(values))} noValidate>
        <div className="rt-auth-field"><label htmlFor="login-email">Email</label>
          <input id="login-email" type="email" autoComplete="email" {...form.register("email")} />
          {fieldError(form.formState.errors.email)}
        </div>
        <div className="rt-auth-field"><label htmlFor="login-password">Password</label>
          <PasswordInput id="login-password" autoComplete="current-password" {...form.register("password")} />
          {fieldError(form.formState.errors.password)}
        </div>
        <Link className="rt-auth-forgot-link" to="/forgot-password">Forgot password?</Link>
        <Button className="rt-auth-full" variant="primary" type="submit" loading={login.isPending}>Sign in</Button>
      </form>
  );

  return (
    <AuthShell pageTitle="Sign in">
      <span className="rt-auth-eyebrow">Private workspace access</span>
      <h1>Welcome to LeadCat CRM</h1>
      <p className="rt-auth-intro">Sign in to capture, organise and follow up on customer enquiries from one private workspace.</p>
      {locationState?.notice ? <InlineNotice title="Sign in required">{locationState.notice}</InlineNotice> : null}
      {login.isError ? <InlineNotice tone="error" title="Unable to sign in">{authError(login.error)}</InlineNotice> : null}
      {sharedLoginAvailable ? (
        <section className="rt-auth-shared" aria-label="LeadCat shared login">
          <a className="rt-auth-primary-link rt-auth-full" href="https://os.leadcat.in/platform/workspaces/entry?signin=1">Continue with LeadCat <span aria-hidden="true">→</span></a>
          <p>Sign in to LeadCat OS, then choose your business and open CRM in a new tab.</p>
          <details className="rt-auth-native-fallback">
            <summary>Use CRM-only login <span aria-hidden="true">⌄</span></summary>
            <p>This signs you into CRM only, using your existing CRM password.</p>
            {nativeLoginForm}
          </details>
        </section>
      ) : nativeLoginForm}
      <div className="rt-auth-trial">
        <strong>New business?</strong>
        <span>
          Create a private workspace, then review Grow pricing and activate your CRM.
        </span>
        <Link className="rt-auth-primary-link" to="/signup">Create workspace</Link>
      </div>
      <nav className="rt-auth-links" aria-label="Legal links">
        <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy</a>
        <a href="/terms.html" target="_blank" rel="noreferrer">Terms</a>
        <a href="/dpa.html" target="_blank" rel="noreferrer">DPA</a>
        <Link to="/account-deletion">Data deletion</Link>
      </nav>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });
  const forgotPassword = useMutation({
    mutationFn: (values: ForgotPasswordValues) => apiRequest<PasswordEmailResponse>("/api/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email: normalizeEmail(values.email) }),
    }),
  });

  return (
    <AuthShell pageTitle="Reset password">
      <span className="rt-auth-eyebrow">Account recovery</span>
      <h1>Reset your password</h1>
      <p className="rt-auth-intro">Enter your login email. If it matches an active account, we will send a secure one-time link.</p>
      {forgotPassword.isSuccess ? (
        <InlineNotice title="Check your email">
          {forgotPassword.data.message}
          {forgotPassword.data.debug_url ? (
            <a className="rt-auth-debug-link" href={forgotPassword.data.debug_url}>Open local test link</a>
          ) : null}
        </InlineNotice>
      ) : null}
      {forgotPassword.isError ? <InlineNotice tone="error" title="Unable to request a reset">{authError(forgotPassword.error)}</InlineNotice> : null}
      <form className="rt-auth-stack" onSubmit={form.handleSubmit((values) => forgotPassword.mutate(values))} noValidate>
        <div className="rt-auth-field">
          <label htmlFor="forgot-password-email">Email</label>
          <input id="forgot-password-email" type="email" autoComplete="email" {...form.register("email")} />
          {fieldError(form.formState.errors.email)}
        </div>
        <Button className="rt-auth-full" variant="primary" type="submit" loading={forgotPassword.isPending}>
          Send password-reset link
        </Button>
      </form>
      <Link className="rt-auth-secondary-link" to="/login">Back to sign in</Link>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() || "";
  const [completed, setCompleted] = useState<PasswordActionCompleteResponse | null>(null);
  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { new_password: "", confirm_password: "" },
  });
  const newPassword = useWatch({ control: form.control, name: "new_password" });
  const details = useQuery({
    queryKey: ["password-action", token],
    queryFn: () => apiRequest<PasswordActionDetails>("/api/auth/password-action/inspect", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
    enabled: Boolean(token) && !completed,
    retry: false,
  });
  const complete = useMutation({
    mutationFn: (values: ResetPasswordValues) => apiRequest<PasswordActionCompleteResponse>("/api/auth/password-action/complete", {
      method: "POST",
      body: JSON.stringify({ token, new_password: values.new_password }),
    }),
    onSuccess: (result) => setCompleted(result),
  });

  const title = details.data?.purpose === "invitation" ? "Create your private password" : "Choose a new password";
  const intro = details.data?.purpose === "invitation"
    ? "Accept your workspace invitation by choosing a password known only to you."
    : "Replace your old password using this secure one-time link.";

  return (
    <AuthShell pageTitle={details.data?.purpose === "invitation" ? "Accept invitation" : "Choose a new password"}>
      <span className="rt-auth-eyebrow">{details.data?.purpose === "invitation" ? "Team invitation" : "Secure password reset"}</span>
      <h1>{completed ? "Password ready" : title}</h1>
      <p className="rt-auth-intro">{completed ? "Your secure link has been used and cannot be opened again." : intro}</p>
      {!token ? <InlineNotice tone="error" title="Link missing">Open the complete link from your invitation or password-reset email.</InlineNotice> : null}
      {details.isPending ? <div className="rt-auth-link-status" role="status">Checking your secure link…</div> : null}
      {details.isError ? <InlineNotice tone="error" title="Link unavailable">{authError(details.error)}</InlineNotice> : null}
      {completed ? (
        <>
          <InlineNotice title="Password updated">{completed.message}</InlineNotice>
          <Link className="rt-auth-primary-link rt-auth-centered-link" to="/login">Sign in</Link>
        </>
      ) : details.data ? (
        <>
          <div className="rt-auth-security-note">
            <span aria-hidden="true">✓</span>
            <p><strong>Private to {details.data.user.name || "you"}</strong>The workspace owner cannot see or retrieve this password.</p>
          </div>
          {complete.isError ? <InlineNotice tone="error" title="Unable to set password">{authError(complete.error)}</InlineNotice> : null}
          <form className="rt-auth-stack" onSubmit={form.handleSubmit((values) => complete.mutate(values))} noValidate>
            <div className="rt-auth-field">
              <label htmlFor="reset-new-password">New password</label>
              <PasswordInput id="reset-new-password" autoComplete="new-password" visibilityLabel="new password" {...form.register("new_password")} />
              {fieldError(form.formState.errors.new_password)}
              <span className="rt-auth-password-rules" aria-label="Password requirements">
                {passwordRules(newPassword).map((rule) => <span className={rule.ok ? "ok" : ""} key={rule.key}>{rule.label}</span>)}
              </span>
            </div>
            <div className="rt-auth-field">
              <label htmlFor="reset-confirm-password">Confirm new password</label>
              <PasswordInput id="reset-confirm-password" autoComplete="new-password" visibilityLabel="confirmed password" {...form.register("confirm_password")} />
              {fieldError(form.formState.errors.confirm_password)}
            </div>
            <Button className="rt-auth-full" variant="primary" type="submit" loading={complete.isPending}>
              {details.data.purpose === "invitation" ? "Set password and accept invitation" : "Set new password"}
            </Button>
          </form>
        </>
      ) : null}
      {!completed ? <Link className="rt-auth-secondary-link" to="/forgot-password">Request a new link</Link> : null}
    </AuthShell>
  );
}

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const locationState = location.state as { from?: string } | null;
  const token = getStoredToken();
  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });
  const newPassword = useWatch({ control: form.control, name: "new_password" });
  const changePassword = useMutation({
    mutationFn: (values: ChangePasswordValues) => apiRequest<AuthResponse>("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({
        current_password: values.current_password,
        new_password: values.new_password,
      }),
    }),
    onSuccess: (result) => {
      queryClient.clear();
      setStoredToken(result.token);
      navigate(safeReturnPath(locationState?.from), {
        replace: true,
        state: { authNotice: "Your private password is now active." },
      });
    },
  });

  if (!token) {
    return <Navigate to="/login" replace state={{ notice: "Sign in with your temporary password to continue." }} />;
  }

  return (
    <AuthShell pageTitle="Secure your account">
      <span className="rt-auth-eyebrow">First sign-in security</span>
      <h1>Create your private password</h1>
      <p className="rt-auth-intro">You signed in with a temporary password. Replace it before entering the CRM workspace.</p>
      <div className="rt-auth-security-note">
        <span aria-hidden="true">✓</span>
        <p><strong>Known only to you</strong>The workspace owner will not see your new password.</p>
      </div>
      {changePassword.isError ? <InlineNotice tone="error" title="Unable to change password">{authError(changePassword.error)}</InlineNotice> : null}
      <form className="rt-auth-stack" onSubmit={form.handleSubmit((values) => changePassword.mutate(values))} noValidate>
        <div className="rt-auth-field">
          <label htmlFor="current-temporary-password">Current temporary password</label>
          <PasswordInput id="current-temporary-password" autoComplete="current-password" visibilityLabel="current temporary password" {...form.register("current_password")} />
          {fieldError(form.formState.errors.current_password)}
        </div>
        <div className="rt-auth-field">
          <label htmlFor="new-private-password">New private password</label>
          <PasswordInput id="new-private-password" autoComplete="new-password" visibilityLabel="new private password" {...form.register("new_password")} />
          {fieldError(form.formState.errors.new_password)}
          <span className="rt-auth-password-rules" aria-label="Password requirements">
            {passwordRules(newPassword).map((rule) => <span className={rule.ok ? "ok" : ""} key={rule.key}>{rule.label}</span>)}
          </span>
        </div>
        <div className="rt-auth-field">
          <label htmlFor="confirm-private-password">Confirm new password</label>
          <PasswordInput id="confirm-private-password" autoComplete="new-password" visibilityLabel="confirmed private password" {...form.register("confirm_password")} />
          {fieldError(form.formState.errors.confirm_password)}
        </div>
        <div className="rt-auth-actions">
          <Button variant="primary" type="submit" loading={changePassword.isPending}>Change password and continue</Button>
          <Button
            variant="secondary"
            type="button"
            disabled={changePassword.isPending}
            onClick={() => {
              queryClient.clear();
              clearStoredToken();
              navigate("/login", { replace: true });
            }}
          >
            Sign out
          </Button>
        </div>
      </form>
    </AuthShell>
  );
}

interface OtpVerificationCardProps {
  destination: string;
  register: UseFormRegister<SignupValues>;
  getValues: UseFormGetValues<SignupValues>;
  fieldError?: FieldError;
  onVerified: (verification: VerifiedOtp) => void;
}

function OtpVerificationCard({ destination, register, getValues, fieldError: codeError, onVerified }: OtpVerificationCardProps) {
  const [sent, setSent] = useState<OtpRequestResponse | null>(null);
  const [verified, setVerified] = useState<VerifiedOtp>(EMPTY_VERIFICATION);
  const [localError, setLocalError] = useState("");
  const otpCooldown = useOtpCooldown();
  const normalizedDestination = normalizeEmail(destination);
  const destinationValid = isValidEmail(normalizedDestination);
  const sentForDestination = sent?.destination === normalizedDestination;
  const verifiedForDestination = Boolean(verified.token && verified.destination === normalizedDestination);
  const requestOtp = useMutation({
    mutationFn: () => apiRequest<OtpRequestResponse>("/api/auth/otp/request", {
      method: "POST",
      body: JSON.stringify({ channel: "email", destination: normalizedDestination }),
    }),
    onSuccess: (result) => {
      setSent(result);
      otpCooldown.start(result.resend_available_in_seconds);
      setVerified(EMPTY_VERIFICATION);
      onVerified(EMPTY_VERIFICATION);
    },
  });
  const verifyOtp = useMutation({
    mutationFn: () => apiRequest<OtpVerifyResponse>("/api/auth/otp/verify", {
      method: "POST",
      body: JSON.stringify({ channel: "email", destination: normalizedDestination, code: getValues("email_otp") }),
    }),
    onSuccess: (result) => {
      const next = { destination: result.destination, token: result.verification_token };
      setVerified(next);
      onVerified(next);
    },
  });
  const busy = requestOtp.isPending || verifyOtp.isPending;
  const error = localError || (requestOtp.isError ? authError(requestOtp.error) : verifyOtp.isError ? authError(verifyOtp.error) : "");

  const send = () => {
    setLocalError("");
    requestOtp.reset();
    verifyOtp.reset();
    if (!destinationValid) {
      setLocalError("Enter a valid email address before sending OTP");
      return;
    }
    setVerified(EMPTY_VERIFICATION);
    onVerified(EMPTY_VERIFICATION);
    requestOtp.mutate();
  };

  const verify = () => {
    setLocalError("");
    verifyOtp.reset();
    const code = String(getValues("email_otp") || "").replace(/\D/g, "");
    if (code.length !== 6) {
      setLocalError("Enter the 6-digit OTP");
      return;
    }
    verifyOtp.mutate();
  };

  return (
    <div className={`rt-auth-verify ${verifiedForDestination ? "verified" : sentForDestination ? "sent" : ""}`} role="group" aria-label="Email verification">
      <div className="rt-auth-verify-head"><strong>Email</strong><span>Email verification</span></div>
      <div className="rt-auth-verify-actions">
        <Button disabled={busy || (sentForDestination && otpCooldown.remainingSeconds > 0)} onClick={send}>
          {sentForDestination && otpCooldown.remainingSeconds > 0
            ? `Resend in ${otpCooldown.remainingSeconds}s`
            : sentForDestination ? "Resend OTP" : "Send OTP"}
        </Button>
        <input
          aria-label="Email OTP"
          inputMode="numeric"
          maxLength={6}
          placeholder="6-digit OTP"
          disabled={!sentForDestination || busy || verifiedForDestination}
          {...register("email_otp")}
        />
        <Button variant="navy" disabled={!sentForDestination || busy || verifiedForDestination} loading={verifyOtp.isPending} onClick={verify}>
          {verifiedForDestination ? "Verified" : "Verify"}
        </Button>
      </div>
      {fieldError(codeError)}
      <div className="rt-auth-verify-status" aria-live="polite">
        {verifiedForDestination
          ? <span className="ok">Verified</span>
          : sentForDestination
            ? <span>{sent?.message || "OTP sent"}{sent?.debug_code ? ` Debug OTP: ${sent.debug_code}` : ""}</span>
            : <span>Verification required</span>}
      </div>
      {error ? <small className="rt-auth-field-error" role="alert">{error}</small> : null}
    </div>
  );
}

export function SignupPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [verification, setVerification] = useState<VerifiedOtp>(EMPTY_VERIFICATION);
  const [verificationError, setVerificationError] = useState("");
  const form = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      business_name: "",
      business_category: "",
      preferred_lead_sources: "Instagram",
      country_code: "IN",
      phone: "",
      email: "",
      email_otp: "",
      password: "",
      terms_accepted: false,
      privacy_acknowledged: false,
      dpa_authority_accepted: false,
    },
  });
  const legalDocuments = useQuery({
    queryKey: ["legal-documents", "current"],
    queryFn: () => apiRequest<CurrentLegalDocumentsResponse>("/api/legal/documents/current"),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const [email = "", password = ""] = useWatch({ control: form.control, name: ["email", "password"] });
  const signup = useMutation({
    mutationFn: (payload: SignupPayload) => apiRequest<AuthResponse>("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
    onSuccess: (result) => {
      recordAnalyticsEvent("sign_up", { method: "email", workspace_product: "leadcat_crm" });
      queryClient.clear();
      setStoredToken(result.token);
      navigate("/setup", {
        replace: true,
        state: {
          authNotice: "Workspace created. Review your business details, then activate Grow.",
        },
      });
    },
  });

  const rememberVerification = (value: VerifiedOtp) => {
    setVerification(value);
    setVerificationError("");
  };

  const submit = (values: SignupValues) => {
    setVerificationError("");
    signup.reset();
    const normalizedEmail = normalizeEmail(values.email);
    const normalizedPhone = normalizePhone(values.phone, values.country_code);
    if (!verification.token || verification.destination !== normalizedEmail) {
      setVerificationError("Please verify your email OTP before creating the workspace");
      return;
    }
    const currentDocuments = legalDocuments.data?.documents || [];
    if (currentDocuments.length !== 3) {
      setVerificationError("Current legal documents could not be verified. Reload signup and try again.");
      return;
    }
    const signupValues = {
      name: values.name,
      business_name: values.business_name,
      business_category: values.business_category,
      preferred_lead_sources: values.preferred_lead_sources,
      country_code: values.country_code,
      phone: values.phone,
      email: values.email,
      email_otp: values.email_otp,
      password: values.password,
    };
    signup.mutate({
      ...signupValues,
      email: normalizedEmail,
      phone: normalizedPhone,
      email_verification_token: verification.token,
      legal_acceptance: {
        documents: currentDocuments.map((document) => ({
          document_type: document.document_type,
          version: document.version,
          content_hash_sha256: document.content_hash_sha256,
          acceptance_action: document.acceptance_action,
          confirmed: true,
          authority_confirmed: document.requires_authority,
        })),
      },
    });
  };

  const currentDocument = (documentType: "terms" | "privacy" | "dpa") =>
    legalDocuments.data?.documents.find((document) => document.document_type === documentType);
  const terms = currentDocument("terms");
  const privacy = currentDocument("privacy");
  const dpa = currentDocument("dpa");

  return (
    <AuthShell pageTitle="Create workspace" signup>
      <span className="rt-auth-eyebrow">Client self-onboarding</span>
      <h1>Create your LeadCat CRM workspace</h1>
      <p className="rt-auth-intro">Create a private workspace for your business enquiries, staff follow-ups, and social media lead capture.</p>
      <div className="rt-auth-progress" aria-label="Signup steps"><span>Business</span><span>Verify</span><span>Secure</span></div>
      {verificationError ? <InlineNotice tone="error" title="Verification required">{verificationError}</InlineNotice> : null}
      {legalDocuments.isError ? <InlineNotice tone="error" title="Legal documents unavailable">{authError(legalDocuments.error)}</InlineNotice> : null}
      {signup.isError ? <InlineNotice tone="error" title="Unable to create workspace">{authError(signup.error)}</InlineNotice> : null}
      <form className="rt-auth-signup-form" onSubmit={form.handleSubmit(submit)} noValidate>
        <div className="rt-auth-business-grid">
          <div className="rt-auth-field rt-auth-half-field"><label htmlFor="signup-name">Owner name</label><input id="signup-name" autoComplete="name" {...form.register("name")} />{fieldError(form.formState.errors.name)}</div>
          <div className="rt-auth-field rt-auth-half-field"><label htmlFor="signup-business">Business name</label><input id="signup-business" autoComplete="organization" {...form.register("business_name")} />{fieldError(form.formState.errors.business_name)}</div>
          <Controller
            control={form.control}
            name="business_category"
            render={({ field, fieldState }) => <BusinessCategoryCombobox
              id="signup-category"
              className="rt-auth-field rt-auth-half-field"
              categories={signupBusinessCategories}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              inputRef={field.ref}
              error={fieldState.error?.message}
              required
            />}
          />
          <Controller
            control={form.control}
            name="preferred_lead_sources"
            render={({ field, fieldState }) => <BusinessCategoryCombobox
              id="signup-source"
              className="rt-auth-field rt-auth-half-field"
              label="Primary lead source"
              categories={signupLeadSources}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              inputRef={field.ref}
              placeholder="Choose primary lead source"
              help="For reference only. You can change this later in Team & Setup."
              error={fieldState.error?.message}
              allowCustom={false}
              searchable={false}
              listboxLabel="Primary lead source options"
              required
            />}
          />
        </div>
        <div className="rt-auth-verification-grid">
          <div className="rt-auth-field rt-auth-phone-field"><label htmlFor="signup-country">Business country</label><select id="signup-country" {...form.register("country_code")}><option value="IN">India</option><option value="AE">United Arab Emirates</option></select><label htmlFor="signup-phone">Phone number</label><input id="signup-phone" type="tel" autoComplete="tel" placeholder="+971 50 123 4567" {...form.register("phone")} />{fieldError(form.formState.errors.phone)}</div>
          <div className="rt-auth-field rt-auth-email-field"><label htmlFor="signup-email">Email address</label><input id="signup-email" type="email" autoComplete="email" placeholder="owner@business.com" {...form.register("email")} />{fieldError(form.formState.errors.email)}
            <OtpVerificationCard destination={email} register={form.register} getValues={form.getValues} fieldError={form.formState.errors.email_otp} onVerified={rememberVerification} />
          </div>
          <div className="rt-auth-field rt-auth-security-field"><label htmlFor="signup-password">Password</label><PasswordInput id="signup-password" autoComplete="new-password" {...form.register("password")} />{fieldError(form.formState.errors.password)}
            <span className="rt-auth-password-rules">{passwordRules(password).map((rule) => <span className={rule.ok ? "ok" : ""} key={rule.key}>{rule.label}</span>)}</span>
          </div>
        </div>
        <fieldset className="rt-auth-legal-consent" disabled={legalDocuments.isPending || legalDocuments.isError}>
          <legend>Review and confirm</legend>
          {legalDocuments.isPending ? <span className="rt-auth-legal-loading">Loading the current legal documents…</span> : null}
          <label>
            <input type="checkbox" {...form.register("terms_accepted")} />
            <span>I accept the <a href={terms?.published_url || "/terms.html"} target="_blank" rel="noreferrer">Terms of Service v{terms?.version || "1.1"}</a>.</span>
          </label>
          {fieldError(form.formState.errors.terms_accepted)}
          <label>
            <input type="checkbox" {...form.register("privacy_acknowledged")} />
            <span>I acknowledge that I have read the <a href={privacy?.published_url || "/privacy.html"} target="_blank" rel="noreferrer">Privacy Policy v{privacy?.version || "1.1"}</a>.</span>
          </label>
          {fieldError(form.formState.errors.privacy_acknowledged)}
          <label>
            <input type="checkbox" {...form.register("dpa_authority_accepted")} />
            <span>I confirm that I am authorized to act for this business and accept the <a href={dpa?.published_url || "/dpa.html"} target="_blank" rel="noreferrer">Data Processing Addendum v{dpa?.version || "1.1"}</a> on its behalf.</span>
          </label>
          {fieldError(form.formState.errors.dpa_authority_accepted)}
        </fieldset>
        <p className="rt-auth-footnote">Creating the workspace does not initiate payment or activate CRM operations. Your acceptance records the exact document versions and cryptographic hashes shown above.</p>
        <div className="rt-auth-actions"><Button variant="primary" type="submit" loading={signup.isPending}>Create workspace</Button><Link className="rt-auth-secondary-link" to="/login">Back to sign in</Link></div>
      </form>
    </AuthShell>
  );
}
